import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { mastersAPI } from "../api/client";

const MastersContext = createContext(null);

export function MastersProvider({ children }) {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    // Only fetch masters when there is an auth token. Without one (e.g. on the
    // login page, or right after logout) the request returns 401, the axios
    // interceptor tries to refresh, and on failure redirects to /login - which
    // re-mounts this provider and triggers an infinite refresh loop. Guarding
    // here breaks that loop: when logged out we simply stay empty and quiet.
    const token = localStorage.getItem("token");
    if (!token) {
      setMasters([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await mastersAPI.getAll({ all: "true" });
      setMasters(res.data || []);
      setError(null);
    } catch (err) {
      console.error("Failed to load masters:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load when the component mounts (only if a token already exists).
    reload();
    // Re-load whenever the token changes (login/logout/refresh) so masters
    // become available right after sign-in without a manual page reload.
    // `storage` fires cross-tab; `hrportal-auth` is dispatched in the same tab
    // by the login handlers (Login.js / OTP.js).
    const handleReload = () => reload();
    window.addEventListener("storage", handleReload);
    window.addEventListener("hrportal-auth", handleReload);
    return () => {
      window.removeEventListener("storage", handleReload);
      window.removeEventListener("hrportal-auth", handleReload);
    };
  }, [reload]);

  const byCategory = useMemo(() => {
    const map = {};
    masters.forEach((m) => {
      if (!map[m.category]) map[m.category] = [];
      map[m.category].push(m);
    });
    Object.keys(map).forEach((cat) => {
      map[cat].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.value).localeCompare(String(b.value)));
    });
    return map;
  }, [masters]);

  // List of active values for a category (plain strings, in order).
  const getList = useCallback(
    (category) => {
      const items = byCategory[category] || [];
      return items.filter((m) => m.is_active).map((m) => m.value);
    },
    [byCategory]
  );

  const createMaster = useCallback(
    async (data) => {
      const res = await mastersAPI.create(data);
      setMasters((prev) => [...prev, res.data]);
      return res.data;
    },
    []
  );

  const updateMaster = useCallback(
    async (id, data) => {
      const res = await mastersAPI.update(id, data);
      setMasters((prev) => prev.map((m) => (m.id === id ? { ...m, ...res.data } : m)));
      return res.data;
    },
    []
  );

  const deleteMaster = useCallback(
    async (id) => {
      await mastersAPI.delete(id);
      setMasters((prev) =>
        prev.map((m) => (m.id === id ? { ...m, is_active: 0 } : m))
      );
    },
    []
  );

  const value = useMemo(
    () => ({ masters, byCategory, loading, error, reload, getList, createMaster, updateMaster, deleteMaster }),
    [masters, byCategory, loading, error, reload, getList, createMaster, updateMaster, deleteMaster]
  );

  return <MastersContext.Provider value={value}>{children}</MastersContext.Provider>;
}

export function useMasters() {
  const ctx = useContext(MastersContext);
  if (!ctx) throw new Error("useMasters must be used within a MastersProvider");
  return ctx;
}
